"use client";

import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";
import {
  Button,
} from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { domId } from "@/lib/dom-id";

interface PaginationProps {
  purpose: string;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

export default function Pagination({
  purpose,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 15, 20, 50, 100],
}: PaginationProps) {
  const t = useTranslations("Pagination");
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    onPageChange(nextPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
      onPageChange(1);
    }
  };

  const createPageRange = () => {
    const range: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i += 1) {
      range.push(i);
    }
    return range;
  };

  const pageRange = createPageRange();
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-start gap-4 pt-3 text-sm text-slate-600">
      {onPageSizeChange && (
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">{t("rowsPerPage")}</span>
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              data-testid={domId("cbo", `${purpose}-rows-per-page-trigger`)}
            >
              <button
                type="button"
                data-testid={domId("cbo", `${purpose}-rows-per-page`)}
                className="flex h-8 min-w-[64px] items-center justify-between gap-1 rounded-lg border border-slate-200 px-3 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none"
              >
                {pageSize}
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[64px] border-none p-1 shadow-lg">
              {pageSizeOptions.map((size) => (
                <DropdownMenuItem
                  data-testid={domId("cbo", `${purpose}-rows-per-page-${size}`)}
                  key={size}
                  onSelect={() => handlePageSizeChange(size)}
                  className={`justify-center ${size === pageSize ? "bg-slate-100 font-medium" : ""}`}
                >
                  {size}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <span className="whitespace-nowrap">
        {t("range", { start: startItem, end: endItem, total })}
      </span>

      <div className="ml-auto flex items-center gap-1">
        <Button
          data-testid={domId("btn", `${purpose}-first-page`)}
          variant="unstyled"
          className="flex h-8 w-8 items-center justify-center rounded text-slate-400 transition disabled:opacity-40"
          onClick={() => goToPage(1)}
          disabled={page === 1}
          aria-label="First page"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </Button>

        <Button
          data-testid={domId("btn", `${purpose}-previous-page`)}
          variant="unstyled"
          className="flex h-8 w-8 items-center justify-center rounded text-slate-400 transition disabled:opacity-40"
          onClick={() => goToPage(page - 1)}
          disabled={page === 1}
          aria-label="Previous page"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Button>

        {pageRange.map((pageNumber, index) => {
          if (index === pageRange.length - 1 && pageNumber < totalPages - 1) {
            return (
              <div key={`range-${pageNumber}`} className="flex items-center gap-1">
                <Button
                  data-testid={domId("btn", `${purpose}-page-${pageNumber}`)}
                  variant="unstyled"
                  onClick={() => goToPage(pageNumber)}
                  className={`flex h-8 w-8 items-center justify-center rounded text-sm font-medium transition ${
                    pageNumber === page
                      ? "border-2 border-blue-600 bg-white text-blue-600"
                      : "text-slate-600"
                  }`}
                >
                  {pageNumber}
                </Button>
                {pageNumber < totalPages - 1 && (
                  <span className="px-1 text-slate-400">...</span>
                )}
              </div>
            );
          }

          return (
            <Button
              key={pageNumber}
              data-testid={domId("btn", `${purpose}-page-${pageNumber}`)}
              variant="unstyled"
              onClick={() => goToPage(pageNumber)}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition ${
                pageNumber === page
                  ? "border border-[#014EA9] bg-white"
                  : "text-slate-600"
              }`}
            >
              {pageNumber}
            </Button>
          );
        })}

        {pageRange[pageRange.length - 1] < totalPages - 1 && (
          <>
            {totalPages - 1 > pageRange[pageRange.length - 1] + 1 && (
              <span className="px-1 text-slate-400">...</span>
            )}
            <Button
              data-testid={domId("btn", `${purpose}-page-${totalPages - 1}`)}
              variant="unstyled"
              onClick={() => goToPage(totalPages - 1)}
              className="flex h-8 w-8 items-center justify-center rounded text-sm font-medium text-slate-600 transition"
            >
              {totalPages - 1}
            </Button>
            <Button
              data-testid={domId("btn", `${purpose}-page-${totalPages}`)}
              variant="unstyled"
              onClick={() => goToPage(totalPages)}
              className="flex h-8 w-8 items-center justify-center rounded text-sm font-medium text-slate-600 transition"
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          data-testid={domId("btn", `${purpose}-next-page`)}
          variant="unstyled"
          className="flex h-8 w-8 items-center justify-center rounded text-slate-400 transition disabled:opacity-40"
          onClick={() => goToPage(page + 1)}
          disabled={page === totalPages}
          aria-label="Next page"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Button>

        <Button
          data-testid={domId("btn", `${purpose}-last-page`)}
          variant="unstyled"
          className="flex h-8 w-8 items-center justify-center rounded text-slate-400 transition disabled:opacity-40"
          onClick={() => goToPage(totalPages)}
          disabled={page === totalPages}
          aria-label="Last page"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          </svg>
        </Button>
      </div>
    </div>
  );
}
